import type { FilterFieldDef, FilterSurface } from './types'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'

export interface FilterOption<TValue extends string> {
    value: TValue
    label: string
}

interface SelectFilterOptions<TValue extends string> {
    label: () => string
    /**
     * Vừa là tập giá trị hợp lệ để parse URL, vừa là nguồn nhãn cho chip —
     * khai một lần thay vì ba lần như trước.
     */
    options: FilterOption<TValue>[] | (() => FilterOption<TValue>[])
    defaultValue: TValue
    /** Ghi đè nhãn chip nếu nhãn option chưa đủ rõ khi đứng một mình. */
    chipLabel?: (value: TValue, optionLabel: string) => string
}

const controlClass = (surface: FilterSurface) => {
    if (surface === 'sheet') return 'h-11 w-full'
    // Ô ngoài render một lần cho cả hai bề mặt → phải responsive (R8).
    if (surface === 'outside') return 'h-11 w-full md:h-9'
    return 'h-9 w-full'
}

/** Bộ lọc dạng chọn một giá trị — loại phổ biến nhất (53 màn đang dùng). */
export function selectFilter<TValue extends string>({
    label,
    options,
    defaultValue,
    chipLabel,
}: SelectFilterOptions<TValue>): FilterFieldDef<TValue> {
    const resolve = () => (typeof options === 'function' ? options() : options)
    const labelOf = (value: TValue) =>
        resolve().find((o) => o.value === value)?.label ?? String(value)

    return {
        defaultValue,
        label,
        parse: (raw) =>
            typeof raw === 'string' && resolve().some((o) => o.value === raw)
                ? (raw as TValue)
                : undefined,
        chipLabel: (value) =>
            chipLabel
                ? chipLabel(value, labelOf(value))
                : `${label()}: ${labelOf(value)}`,
        render: ({ id, value, onChange, surface }) => (
            <Select
                value={value}
                onValueChange={(next) => onChange(next as TValue)}
            >
                <SelectTrigger id={id} className={controlClass(surface)}>
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {resolve().map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        ),
    }
}
